import React, { useState, useLayoutEffect, useContext, useEffect, useCallback, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import ApiDevice from "../../../app/api/admin/device/ApiDevice";
import ApiAppInit from "../../../app/api/admin/system/ApiAppInit";
import ApiTask from "../../../app/api/admin/task/ApiTask";
import Page from "../../modules/page";
import { Link } from "react-router-dom";
import { ContextContent } from "../../modules/content/contentContext";
import { ContextPage } from "../../modules/page/pageContext";
import List from "../../modules/list";
import ListHeaderItem from "../../modules/list/__header-item";
import ListBodyItem from "../../modules/list/__body-item";
import InputNumber from "../../components/inputs/number/number";
import InputText from "../../components/inputs/text";
import Select from "../../components/inputs/select";
import InputCheckbox from "../../components/inputs/checkbox";
import Loader from "../../components/loader";
import Button from "../../components/button";
import Modal from "../../components/modals/modal";
import Window from "../../components/window";
import { ContextList } from "../../modules/list/listContext";
import useListFilters from "../../modules/list/useListFilters";
import CommentCell from "../../modules/comment-cell";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DvrIcon from "@mui/icons-material/Dvr";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReplayIcon from "@mui/icons-material/Replay";
import BoltIcon from "@mui/icons-material/Bolt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CONFIG from "../../../inc/Config";
import Routes from "../../../inc/Routes";
import DateLib from "../../../inc/DateLib";
import StorageLocal from "../../../inc/StorageLocal";
import { useIsNarrow, isNarrowScreen } from "../../../inc/Responsive";
import DevicesExport from "./export";
import DeviceTransferModal from "./transfer";
import {
    buildCatalog, needsConfirm, summarizeDevices, actionHint, PANEL_MODAL_ACTIONS, IMMEDIATE_NOTE,
    BatchConfirmModal, BatchApkModal, BatchMoreMenu, BatchActionButton, BatchDangerZone, BatchSummary,
    BatchProgress, isLinkAllowed, isLinkRequired, actionButtonClass,
} from "./batch-actions";
import "./styles.css";

// Polling статусов batch-скриптов, пока хоть один выбранный ещё pending/running.
const SCRIPT_POLL_MS = 3000;

// Снимки конкретного устройства — это раздел «Скриншоты» с готовым фильтром: он читает
// device_id из URL (см. pages/screenshots/index.js). Отдельной страницы под устройство нет
// намеренно — выдача, пагинация и карточка снимка там уже есть, дублировать их незачем.
export const screenshotsUrl = (deviceId) =>
    `${Routes.parseName('screenshots.index')}?device_id=${encodeURIComponent(deviceId)}`;

// Замеры устройства — раздел «Бенчмарки» с тем же готовым фильтром device_id из URL.
const benchmarksUrl = (deviceId) =>
    `${Routes.parseName('benchmarks.index')}?device_id=${encodeURIComponent(deviceId)}`;

// Выбранный режим отображения списка устройств (таблица / плитка снимков) — переживает перезагрузку.
const VIEW_MODE_STORAGE_KEY = 'DEVICES_VIEW_MODE';

// Модификатор бейджа по статусу устройства (статусы API: new/queued/installing/active/stopped).
// Фильтр «Управление»: помечено ли устройство ручным (devices.is_manual). Значения уезжают в
// DeviceFilter::isManual как есть.
const MANUAL_FILTER_OPTIONS = {
    '1': 'Ручное',
    '0': 'Автоматическое',
};

// Фильтр «Статус аккаунта»: ключи уходят в DeviceFilter::accountStatus, тот сверяет их с подписями
// статусов сервиса аккаунтов. Только три статуса — остальные на этой странице не нужны.
const ACCOUNT_STATUS_FILTER_OPTIONS = {
    'working': 'В работе',
    'idle': 'Остановлен',
    'locked': 'Заблокирован',
};

const STATUS_BADGES = {
    'active': 'badge_online',
    'stopped': 'badge_offline',
    'new': 'badge_warn',
    'queued': 'badge_warn',
    'installing': 'badge_warn',
    'rebooting': 'badge_warn',
    'resetting': 'badge_warn',
    'error': 'badge_offline',
};

// Статусы, при которых на устройстве идёт фоновая задача: установка или снятие прокси.
const PROXY_PENDING_STATUSES = ['setup', 'removing'];

// Пока хоть на одном устройстве прокси ставится (5–15 мин) или снимается, перечитываем список —
// иначе статус висел бы до ручного обновления.
const PROXY_SETUP_POLL_MS = 20000;

// Давность снимка в плитке: до 22 мин — свежий, до 16 ч 2 мин включительно — жёлтый, дольше —
// красный. Пороги — от оператора: снимки крон делает по расписанию, и отставание больше этих
// интервалов значит, что устройство не снимается (лежит, занято, потеряно).
const SCREENSHOT_WARN_AFTER_MS = 22 * 60 * 1000;
const SCREENSHOT_STALE_AFTER_MS = ((16 * 60) + 2) * 60 * 1000;
// Время в плитке тикает само — одним таймером на страницу, не на плитку.
const SCREENS_CLOCK_MS = 30 * 1000;

// Статусы запуска скрипта (device_script_executes/redroid_script_executes.status) — своя
// колонка «Последний скрипт», не путать со STATUS_BADGES устройства выше.
const SCRIPT_STATUS_LABELS = {
    'pending': 'в очереди',
    'running': 'выполняется',
    'success': 'успешно',
    'error': 'ошибка',
    'cancelled': 'отменён',
};

// Список устройств: сортировка/фильтры по DeviceFilter (id/name/status/node_id/device_type_id/external_id).
export default function DevicesIndex({ ...other }) {
    return <Page {...other}>
        <DevicesIndexContent/>
    </Page>;
}

function DevicesIndexContent() {
    const contextContent = useContext(ContextContent);
    const contextPage = useContext(ContextPage);
    const deviceStatuses = useSelector((state) => state.option.deviceStatuses);
    const nodes = useSelector((state) => state.option.nodes);
    const deviceTypes = useSelector((state) => state.option.deviceTypes);
    const proxies = useSelector((state) => state.option.proxies);
    const deviceProxyStatuses = useSelector((state) => state.option.deviceProxyStatuses);
    const deviceProxyModes = useSelector((state) => state.option.deviceProxyModes);
    const deviceHealthStatuses = useSelector((state) => state.option.deviceHealthStatuses);
    // Прокси могли завести/выключить в разделе «Прокси» уже после входа — освежаем справочник.
    useEffect(() => { ApiAppInit.ReloadProxies(); }, []);
    const accessToken = useSelector((state) => state.app.accessToken);

    // Батч-выбор устройств чекбоксами (только текущая страница списка) — установка APK
    // и запуск adb-скриптов на нескольких устройствах разом (DeviceScriptsController).
    const [selectedIds, setSelectedIds] = useState([]);
    const toggleSelect = (id, checked) => {
        setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
    };
    const canBatch = contextContent.checkPermission('edit');

    // Аккаунты сервиса acc.rbx.effps.com — {id: {login, bound_device}}, грузим один раз (список
    // небольшой, ~сотня записей) вместо запроса на каждую строку. bound_device — id+name
    // устройства, где аккаунт СЕЙЧАС занят (см. AccountsController::index на бэке) — по нему
    // AccountModal решает, какие варианты заблокировать (заняты другим устройством).
    // Перечитываем не только на входе: после перепривязки bound_device меняется у ДВУХ аккаунтов
    // (у нового и у отпущенного), и без обновления карты соседние строки до перезагрузки страницы
    // помечали бы аккаунт занятым устройством, за которым он уже не числится.
    const [accountsMap, setAccountsMap] = useState({});
    const reloadAccounts = useCallback(async () => {
        const data = await ApiDevice.Accounts();
        if (!data || !Array.isArray(data.data)) return;
        const map = {};
        data.data.forEach((account) => { map[String(account.id)] = account; });
        setAccountsMap(map);
    }, []);
    useEffect(() => { reloadAccounts(); }, [reloadAccounts]);

    // Режим отображения списка: 'table' (как было) либо 'screens' (плитка снимков экрана).
    // Держим в localStorage — на мониторинге устройств режим выбирают один раз и надолго,
    // сброс на каждую перезагрузку раздражал бы. Тот же приём, что с интервалами автообновления.
    const [_viewMode, set_viewMode] = useState(() => StorageLocal.Get(VIEW_MODE_STORAGE_KEY, 'table'));
    const setViewMode = (mode) => {
        set_viewMode(mode);
        StorageLocal.Set(VIEW_MODE_STORAGE_KEY, mode);
    };

    // Открытые окна живого экрана — массив id устройств (по окну на устройство).
    // Только на широком экране: на телефоне окно 400x720 поверх 360px бесполезно, там стрим
    // открывается новой вкладкой (см. isNarrow ниже).
    const [liveDevices, setLiveDevices] = useState([]);
    const openLive = (id) => setLiveDevices((prev) => (prev.includes(id) ? prev : [...prev, id]));
    const closeLive = (id) => setLiveDevices((prev) => prev.filter((item) => item !== id));
    const isNarrow = useIsNarrow();

    // Ссылка на страницу стрима: тот же origin, что и API (CONFIG.serverUrl — в проде
    // пусто = same-origin). Токен — ТОЛЬКО access, в query (навигация iframe/вкладки
    // не умеет слать заголовки); страница стрима сама разносит его по каналам.
    const liveUrl = (id) => `${CONFIG.serverUrl}/control/${id}/live?token=${encodeURIComponent(accessToken ?? '')}`;

    // Только активные показывает пункт меню — он ведёт на /devices?status=active (menu.js). Сам
    // список без параметров — все устройства: иначе смотреть все было бы нечем.
    const filter = useListFilters(['id', 'name', 'status', 'node_id', 'device_type_id', 'external_id', 'account_status', 'is_manual']);

    // Опции фильтра «Аккаунт»: id + login (account_name в сервисе аккаунтов). Select уже умеет
    // искать по подписи в поле внутри дропдауна — отдельный autocomplete не нужен.
    const accountFilterOptions = useMemo(() => {
        const options = {};
        Object.entries(accountsMap)
            .sort(([leftId], [rightId]) => Number(leftId) - Number(rightId))
            .forEach(([id, account]) => {
                const accountName = account.login ?? '';
                options[id] = `${id} — ${accountName}`;
            });
        return options;
    }, [accountsMap]);

    useLayoutEffect(() => {
        contextContent.setTitle('Устройства');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const header = [
        ...(canBatch ? [
            <ListHeaderItem key="select" name="select" isSort={false} isCardHead={true} isAction={true}>
                {/* Подпись видна только в мобильной полоске действий (в шапке таблицы её
                    прячет CSS): там чекбокс стоит сам по себе и без слов не читается. */}
                <SelectAllCheckbox selectedIds={selectedIds} setSelectedIds={setSelectedIds}>
                    Выбрать все
                </SelectAllCheckbox>
            </ListHeaderItem>,
        ] : []),
        // Флаги isCard*/isSecondary — раскладка карточки на телефоне (в таблице ничего не
        // меняют, см. modules/table/table.js): первая строка «чекбокс · id · имя · статус»,
        // затем нода/аккаунт/последний скрипт, служебные поля под «Подробнее», стрим — низом.
        // Состав — по разбору с UI/UX и критиком: служебные ADB-порт и провижн — в карточке
        // устройства (провижн, если устарел, — бейджем в «Состоянии»), тариф — второй строкой
        // «Ноды» (там же его и меняют). Ширины и плотность — table_dense в styles.css.
        <ListHeaderItem key="id" name="id" isCardHead={true}>ID</ListHeaderItem>,
        <ListHeaderItem key="name" name="name" isCardHead={true}>Имя</ListHeaderItem>,
        // «Состояние» сортируется по статусу; по режиму управления — фильтр «Управление».
        <ListHeaderItem key="status" name="status" isCardHead={true}>Состояние</ListHeaderItem>,
        <ListHeaderItem key="node_name" name="node_name">Нода</ListHeaderItem>,
        <ListHeaderItem key="external_id" name="external_id" isCardWide={true}>Аккаунт</ListHeaderItem>,
        // Прокси сортируются как их подпись в app-init — по региону выхода, затем по адресу.
        <ListHeaderItem key="proxy" name="proxy_id" isCardWide={true}>Прокси</ListHeaderItem>,
        // Последний скрипт — по времени запуска, замер — по баллу. Пустые (null) бэкенд всегда
        // ставит в конец, в любую сторону.
        <ListHeaderItem key="last_script" name="last_script" isCardWide={true}>Последний скрипт</ListHeaderItem>,
        <ListHeaderItem key="last_benchmark" name="last_benchmark" isSecondary={true}>Замер</ListHeaderItem>,
        <ListHeaderItem key="comment" name="comment" isSecondary={true}>Заметка</ListHeaderItem>,
        <ListHeaderItem key="live" name="live" isSort={false} isCardFoot={true}>Стрим</ListHeaderItem>,
    ];

    const filters = [
        <InputNumber className="input_wf" value={filter.values.id} onChange={(e) => filter.setValue('id', e.target.value)}>id</InputNumber>,
        <InputText className="input_wf" value={filter.values.name} onChange={(e) => filter.setValue('name', e.target.value)}>Имя</InputText>,
        <Select className="input_wf" options={deviceStatuses} value={filter.values.status} isNull={true}
            onChange={(e) => filter.setValue('status', e.target.value)}>Статус</Select>,
        <Select className="input_wf" options={nodes} value={filter.values.node_id} isNull={true}
            onChange={(e) => filter.setValue('node_id', e.target.value)}>Нода</Select>,
        <Select className="input_wf" options={deviceTypes} value={filter.values.device_type_id} isNull={true}
            onChange={(e) => filter.setValue('device_type_id', e.target.value)}>Тип</Select>,
        <Select className="input_wf" options={accountFilterOptions} value={filter.values.external_id} isNull={true}
            onChange={(e) => filter.setValue('external_id', e.target.value)}>Аккаунт</Select>,
        <Select className="input_wf" options={ACCOUNT_STATUS_FILTER_OPTIONS} value={filter.values.account_status} isNull={true}
            onChange={(e) => filter.setValue('account_status', e.target.value)}>Статус аккаунта</Select>,
        // Пусто — все: пустое значение до фильтра не доходит (BaseFilter::apply его пропускает).
        <Select className="input_wf" options={MANUAL_FILTER_OPTIONS} value={filter.values.is_manual} isNull={true}
            onChange={(e) => filter.setValue('is_manual', e.target.value)}>Управление</Select>,
    ];

    const renderRow = (row) => [
        ...(canBatch ? [
            <ListBodyItem key="select">
                <InputCheckbox value={selectedIds.includes(row.id)}
                    onChange={(e) => toggleSelect(row.id, e.target.checked)}/>
            </ListBodyItem>,
        ] : []),
        <ListBodyItem key="id">
            <DeviceIdCell device={row} isNarrow={isNarrow}
                url={contextContent.checkPermission('show') ? contextPage.routes.show.url({ id: row.id }) : null}/>
        </ListBodyItem>,
        <ListBodyItem key="name"><NameCell device={row} canEdit={canBatch}/></ListBodyItem>,
        // Сначала статус, чтобы бейджи статуса стояли ровно по вертикали; за ним — только
        // проблемы (здоровье repair/error, устаревший провижн); режим управления — в конце.
        <ListBodyItem key="status">
            <span className="devices__state">
                <span className={`badge ${STATUS_BADGES[row.status] ?? ''}`}>
                    {deviceStatuses[row.status] ?? row.status}
                </span>
                <HealthBadge device={row} deviceHealthStatuses={deviceHealthStatuses}/>
                <ProvisionOutdatedBadge version={row.provision_version}/>
                <ControlModeBadge device={row} canEdit={canBatch}/>
            </span>
        </ListBodyItem>,
        <ListBodyItem key="node_name">
            <span className="devices__node">
                <span className="devices__node-name">{row.node_name ?? '—'}</span>
                <span className="devices__node-type">
                    <TypeCell device={row} canEdit={canBatch} deviceTypes={deviceTypes}/>
                </span>
            </span>
        </ListBodyItem>,
        <ListBodyItem key="external_id"><AccountCell device={row} canEdit={canBatch} accounts={accountsMap} onRebind={reloadAccounts}/></ListBodyItem>,
        <ListBodyItem key="proxy">
            <ProxyCell device={row} canEdit={canBatch} proxies={proxies} deviceProxyStatuses={deviceProxyStatuses}
                deviceProxyModes={deviceProxyModes}/>
        </ListBodyItem>,
        <ListBodyItem key="last_script"><LastScriptCell lastScript={row.last_script}/></ListBodyItem>,
        <ListBodyItem key="last_benchmark"><LastBenchmarkCell device={row}/></ListBodyItem>,
        <ListBodyItem key="comment"><DeviceCommentCell device={row} canEdit={canBatch}/></ListBodyItem>,
        <ListBodyItem key="live">
            {/* Телефон — одна кнопка «Стрим» в новую вкладку (встроенное окно там нечитаемо).
                Широкий экран — как было: новая вкладка или окно поверх списка. */}
            {isNarrow
                ? <a
                    className="devices__live-btn"
                    href={liveUrl(row.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Открыть стрим в новой вкладке"
                >
                    <OpenInNewIcon fontSize="small"/>
                    Стрим
                </a>
                : <span className="devices__row-actions">
                    <a
                        className="devices__icon-action"
                        href={liveUrl(row.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Открыть стрим в новой вкладке"
                    >
                        <OpenInNewIcon fontSize="small"/>
                    </a>
                    <button
                        type="button"
                        className="devices__icon-action"
                        onClick={() => openLive(row.id)}
                        title="Открыть стрим в окне"
                    >
                        <DvrIcon fontSize="small"/>
                    </button>
                    {/* Снимки этого устройства — в разделе «Скриншоты» с готовым фильтром.
                        Соседство со стримом не случайно: и то, и другое про экран устройства. */}
                    <Link
                        className="devices__icon-action"
                        to={screenshotsUrl(row.id)}
                        title="Снимки экрана устройства"
                    >
                        <PhotoLibraryIcon fontSize="small"/>
                    </Link>
                </span>}
        </ListBodyItem>,
    ];

    // Плитка снимков экрана вместо таблицы. Оставляем то, ради чего в неё и заходят:
    // сам кадр, кто это, живой ли, и возможность выбрать устройство для батч-действий.
    // Инлайн-правки имени/типа/аккаунта тут сознательно нет — за ней в табличный режим.
    // Давность снимков в плитке тикает сама; в таблице часы стоят.
    const now = useNow((_viewMode === 'screens') ? SCREENS_CLOCK_MS : null);

    const renderScreens = (rows) => <>
        {/* Аналог чекбокса «выбрать все» из шапки таблицы — иначе в этом режиме батч-действия
            пришлось бы набирать поштучно. Живёт внутри renderList, т.к. читает ContextList. */}
        {canBatch ? <div className="devices__screens-bar">
            <SelectAllCheckbox selectedIds={selectedIds} setSelectedIds={setSelectedIds}/>
            <span>Выбрать все на странице</span>
        </div> : null}
        <div className="devices__screens">
            {rows.map((row) => {
                const freshness = screenshotFreshness(row.screenshot_at, now);
                return <div
                    key={row.id}
                    className={`devices__screen devices__screen_${freshness.level}${selectedIds.includes(row.id) ? ' devices__screen_selected' : ''}`}
                >
                    <div className="devices__screen-head">
                        {canBatch
                            ? <InputCheckbox value={selectedIds.includes(row.id)}
                                onChange={(e) => toggleSelect(row.id, e.target.checked)}/>
                            : null}
                        <span className="devices__screen-name" title={`#${row.id} ${row.name ?? ''}`}>
                            {row.name || `#${row.id}`}
                        </span>
                        {/* Режим — только для чтения: инлайн-правок в плитке нет, за ними — в таблицу. */}
                        <ControlModeBadge device={row} canEdit={false}/>
                        <span className={`badge ${STATUS_BADGES[row.status] ?? ''}`}>
                            {deviceStatuses[row.status] ?? row.status}
                        </span>
                    </div>

                    {/* Снимок перезаписывается по постоянному пути, URL не меняется — без метки
                        времени в query браузер показывал бы прошлый кадр из кеша. */}
                    {row.screenshot_url
                        ? (isNarrow
                            ? <a
                                className="devices__screen-shot"
                                href={liveUrl(row.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Открыть живой экран в новой вкладке"
                            >
                                <img
                                    src={`${row.screenshot_url}?v=${encodeURIComponent(row.screenshot_at ?? '')}`}
                                    alt={`Экран устройства ${row.name || row.id}`}
                                    loading="lazy"
                                />
                            </a>
                            : <button
                                type="button"
                                className="devices__screen-shot"
                                onClick={() => openLive(row.id)}
                                title="Открыть живой экран в окне"
                            >
                                <img
                                    src={`${row.screenshot_url}?v=${encodeURIComponent(row.screenshot_at ?? '')}`}
                                    alt={`Экран устройства ${row.name || row.id}`}
                                    loading="lazy"
                                />
                            </button>)
                        : <div className="devices__screen-shot devices__screen-shot_empty">Снимка нет</div>}

                    {/* Строки есть всегда (пустые — с заглушкой): иначе плитки в ряду разной высоты. */}
                    <ScreenAccountLine device={row} accounts={accountsMap} isNarrow={isNarrow}/>
                    <ScreenScriptLine lastScript={row.last_script} isNarrow={isNarrow}/>

                    <div className="devices__screen-foot">
                        <ScreenAge screenshotAt={row.screenshot_at} freshness={freshness} isNarrow={isNarrow}/>
                        <a
                            className={isNarrow ? 'devices__live-btn' : 'devices__icon-action'}
                            href={liveUrl(row.id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Открыть стрим в новой вкладке"
                        >
                            <OpenInNewIcon fontSize="small"/>
                            {isNarrow ? 'Стрим' : null}
                        </a>
                    </div>
                </div>;
            })}
            {rows.length === 0 ? <div className="devices__screens-empty">Устройств не найдено</div> : null}
        </div>
    </>;

    const isScreens = _viewMode === 'screens';

    return <>
        <div className="devices__view-toggle">
            {/* Один переключатель вместо пары «Таблица»/«Скриншоты»: кнопка текущего режима
                выглядела рабочей, а нажатие на неё ничего не меняло. Нажата (подсвечена) —
                плитка снимков, отжата — таблица. */}
            <Button className={isScreens ? 'btn-success' : ''}
                onClick={() => setViewMode(isScreens ? 'table' : 'screens')}>
                Скриншоты
            </Button>
            <DevicesExport appliedFilters={filter.applied} selectedIds={selectedIds}
                accounts={accountsMap} scriptStatuses={SCRIPT_STATUS_LABELS}/>
        </div>
        <List
            header={header}
            ApiModel={ApiDevice}
            renderRow={renderRow}
            renderList={isScreens ? renderScreens : undefined}
            filters={filters}
            defaultSortKey="id"
            defaultSortIsAsc={true}
            tableClassName="table_dense"
            selectedIds={selectedIds}
            searchParams={filter.applied}
            onApplyFilters={filter.apply}
            onResetFilters={filter.reset}
        >
            {/* В плитке снимков колонки прокси нет — и перечитывать ради неё незачем. */}
            {!isScreens ? <ProxySetupPoller/> : null}
            {canBatch && selectedIds.length > 0
                ? <DevicesBatchBar selectedIds={selectedIds} onClear={() => setSelectedIds([])}
                    onDeselect={(ids) => setSelectedIds((previous) => previous.filter((id) => !ids.includes(id)))}
                    onAccountsChanged={reloadAccounts}/> : null}
        </List>
        {liveDevices.map((deviceId, index) => (
            <Window
                key={deviceId}
                title={`Живой экран устройства #${deviceId}`}
                initialX={96 + index * 34}
                initialY={96 + index * 34}
                initialWidth={400}
                initialHeight={720}
                onClose={() => closeLive(deviceId)}
            >
                {/* key = deviceId+token: ремоунт iframe при ротации access-токена
                    (внутри iframe токен не обновляется — после TTL пойдут 401). */}
                <iframe
                    key={`${deviceId}-${accessToken}`}
                    className="devices__live-frame"
                    src={liveUrl(deviceId)}
                    title={`Стрим устройства #${deviceId}`}
                    allow="autoplay"
                />
            </Window>
        ))}
    </>;
}

// Инлайн-редактирование имени устройства: клик по ячейке → input, Enter/blur — сохранить
// (PATCH devices/{id}), Esc — отмена. После сохранения перезагружаем список (contextList.reload).
function NameCell({ device, canEdit }) {
    const contextList = useContext(ContextList);
    const [_editing, set_editing] = useState(false);
    const [_value, set_value] = useState(device.name || '');
    const [_saving, set_saving] = useState(false);

    useEffect(() => { set_value(device.name || ''); }, [device.name]);

    const save = async () => {
        if (_saving) return;
        if (_value.trim() === '' || _value === device.name) { set_value(device.name || ''); set_editing(false); return; }
        set_saving(true);
        const data = await ApiDevice.Update(device.id, { name: _value }).getResponse();
        set_saving(false);
        set_editing(false);
        if (data) contextList.reload();
        else set_value(device.name || '');
    };

    if (!canEdit) return <span className="devices__name" title={device.name}>{device.name}</span>;

    if (!_editing) {
        return <span className="devices__editable devices__name" title={`${device.name} — клик, чтобы изменить`}
            onClick={() => set_editing(true)}>{device.name}</span>;
    }

    return <input
        autoFocus
        className="input devices__inline-input"
        value={_value}
        disabled={_saving}
        onChange={(e) => set_value(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); save(); }
            if (e.key === 'Escape') { set_value(device.name || ''); set_editing(false); }
        }}
    />;
}

// Заметка устройства: PATCH devices/{id} с одним полем comment (остальные поля sometimes,
// см. DeviceUpdateRequest на бэке). После сохранения перезагружаем список.
function DeviceCommentCell({ device, canEdit }) {
    const contextList = useContext(ContextList);

    return <CommentCell
        value={device.comment}
        canEdit={canEdit}
        onSave={async (value) => {
            const data = await ApiDevice.Update(device.id, { comment: value }).getResponse();
            if (data) contextList.reload();
            return Boolean(data);
        }}
    />;
}

// Режим управления в ячейке статуса. Ручное — устройство выведено из-под расписания слоя
// агентов: его не поднимает и не опускает планировщик, и вебхук запуска воркера на него не идёт.
//
// Режим виден у каждой строки: ручное — жёлтый бейдж «Ручное» с рукой, автоматика —
// приглушённая иконка робота без подписи (это норма, у большинства строк — не шуметь текстом).
// Клик по «Ручное» снимает пометку (самое частое действие — вернуть устройство в оборот); ставят
// её пачкой из панели действий, туда же входит и одно выбранное устройство.
function ControlModeBadge({ device, canEdit }) {
    const contextList = useContext(ContextList);
    const [_saving, set_saving] = useState(false);

    if (!device.is_manual) {
        return <span className="devices__control-mode devices__control-mode_auto" role="img"
            aria-label="Автоматическое управление"
            title="Автоматическое управление: устройство поднимает и опускает планировщик">
            <SmartToyOutlinedIcon fontSize="inherit"/>
        </span>;
    }

    const manualContent = <>
        <PanToolOutlinedIcon fontSize="inherit"/>
        {_saving ? '…' : 'Ручное'}
    </>;

    if (!canEdit) {
        return <span className="badge badge_warn devices__control-mode"
            title="Ручное управление: планировщик устройство не трогает">
            {manualContent}
        </span>;
    }

    const unset = async () => {
        if (_saving) return;
        set_saving(true);
        const data = await ApiDevice.Update(device.id, { is_manual: false }).getResponse();
        set_saving(false);
        if (data) contextList.reload();
    };

    return <span
        className="badge badge_warn devices__control-mode devices__editable"
        title="Ручное управление: планировщик устройство не трогает. Клик — вернуть в автоматику"
        onClick={unset}
    >
        {manualContent}
    </span>;
}

// Здоровье по последнему аудиту ноды (health_status). Аудит ловит «в панели работает, а на деле
// нет» и ставит repair — идёт «Исправление устройства» (status на это время rebooting); error — не
// ожило (status тоже error). В таблице — только они: ok у сотни здоровых был бы шумом (в карточке
// устройства здоровье показано всегда). null — аудит ещё не проверял. Поле — про последний аудит,
// а не про текущую секунду.
const HEALTH_BADGES = {
    'repair': 'badge_warn',
    'error': 'badge_offline',
};

function HealthBadge({ device, deviceHealthStatuses }) {
    if (!(device.health_status in HEALTH_BADGES)) return null;
    return <span
        className={`badge ${HEALTH_BADGES[device.health_status]}`}
        title="По последнему аудиту ноды; ход и причина — в задаче «Исправление устройства»"
    >
        {deviceHealthStatuses[device.health_status] ?? device.health_status}
    </span>;
}

// Ячейка «ID»: номер ведёт на карточку устройства (/devices/{id}, см. pages.js — роут show
// зарегистрирован). Ссылку вешаем на сам номер, а не заводим колонку-«глаз», как в списках нод
// и пользователей: колонок тут и так полтора десятка, а id в строке ровно один и по смыслу
// именно он и есть «эта запись». url === null — нет права show, тогда просто текст.
//
// В узком режиме к номеру добавляется решётка: в карточке телефона первая строка идёт без
// подписей, и без неё id не отличить от прочих чисел строки.
function DeviceIdCell({ device, isNarrow, url }) {
    const label = isNarrow ? `#${device.id}` : device.id;

    if (!url) return <span>{label}</span>;

    return <Link to={url} title="Открыть карточку устройства">{label}</Link>;
}

// Ячейка «Тип»: клик → модалка со Select тарифов, «Применить» → пересборка контейнера под
// новые ресурсы (POST devices/{id}/change-type → run.py update). Долгая синхронная операция.
function TypeCell({ device, canEdit, deviceTypes }) {
    const contextList = useContext(ContextList);
    const [_open, set_open] = useState(false);

    if (!canEdit) return <span>{device.device_type_code ?? '—'}</span>;

    return <>
        <span className="devices__editable" title="Сменить тариф (пересборка)" onClick={() => set_open(true)}>
            {device.device_type_code ?? '—'}
        </span>
        {_open ? (
            <ChangeTypeModal
                device={device}
                deviceTypes={deviceTypes}
                onClose={() => set_open(false)}
                onDone={() => contextList.reload()}
            />
        ) : null}
    </>;
}

function ChangeTypeModal({ device, deviceTypes, onClose, onDone }) {
    const [_busy, set_busy] = useState(false);
    const [_queued, set_queued] = useState(false);
    const [_error, set_error] = useState(null);

    // Выбор тарифа из селекта сразу создаёт задачу — без отдельной кнопки «Применить».
    const handlePick = async (deviceTypeId) => {
        if (deviceTypeId === '' || _busy) return;
        set_busy(true);
        set_error(null);
        try {
            const data = await ApiDevice.ChangeType(device.id, deviceTypeId).getResponse();
            if (data && data.result) {
                set_queued(true);
                onDone(); // перезагрузить список — статус устройства станет «В очереди»
            } else {
                set_error((data && data.error) || 'Не удалось создать задачу');
            }
        } finally {
            set_busy(false);
        }
    };

    return <Modal isOpenModal={true} closeModal={onClose} title={`Сменить тариф: ${device.name}`}>
        {_queued ? (
            <div className="devices__change-type-hint">
                Задача создана — пересборка идёт в фоне (крон, ~1–2 минуты). Прогресс виден по статусу
                устройства: «В очереди» → «Установка» → «Активен». Модалку можно закрыть.
            </div>
        ) : (
            <>
                <div className="devices__change-type-hint">
                    Выберите новый тариф — задача на пересборку создастся сразу. Данные устройства
                    сохраняются, выполняется в фоне.
                </div>
                <Select className="input_wf" options={deviceTypes} value="" isNull={true} readOnly={_busy}
                    onChange={(e) => handlePick(e.target.value)}>Тариф</Select>
                {_busy ? <div className="devices__change-type-hint">Создание задачи…</div> : null}
            </>
        )}
        {_error ? <div className="devices__change-type-error">{_error}</div> : null}
    </Modal>;
}

// «Сейчас» для тикающих меток: одно состояние на страницу, обновляется раз в intervalMs, пока
// вкладка видна; вернулись на вкладку — пересчёт сразу. intervalMs = null — часы стоят (таблица:
// там тикать нечему, и лишний ререндер раз в 30 с незачем).
function useNow(intervalMs) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!intervalMs) return undefined;
        const tick = () => { if (!document.hidden) setNow(Date.now()); };
        const timer = setInterval(tick, intervalMs);
        document.addEventListener('visibilitychange', tick);
        tick();
        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', tick);
        };
    }, [intervalMs]);

    return now;
}

// «только что» / «23 мин» / «5 ч 12 мин» / «3 д». Минуты у часов нужны: рядом с порогом 16 ч 2 мин
// «16 ч» было бы и жёлтым, и красным. isShort — телефон: узкая плитка, минуты у часов опускаем
// (там цвет различают иконка и жирность).
function formatAge(ageMs, isShort) {
    const minutes = Math.floor(ageMs / 60000);
    if (minutes < 1) return 'только что';
    if (minutes < 60) return `${minutes} мин`;
    const hours = Math.floor(minutes / 60);
    if (hours < 48) return (isShort || (minutes % 60) === 0) ? `${hours} ч` : `${hours} ч ${minutes % 60} мин`;
    return `${Math.floor(hours / 24)} д`;
}

// Давность снимка: level — fresh | warn | stale. Снимка нет — stale: для оператора это худший
// случай «снимок не обновляется». Часы разошлись (снимок «из будущего») — считаем свежим.
function screenshotFreshness(screenshotAt, now) {
    const date = DateLib.parse(screenshotAt);
    if (date === null) return { level: 'stale', ageMs: null };
    // До целых минут — как в подписи: иначе «16 ч 2 мин» успевало побыть и жёлтым, и красным.
    const ageMs = Math.floor(Math.max(0, now - date.getTime()) / 60000) * 60000;
    if (ageMs < SCREENSHOT_WARN_AFTER_MS) return { level: 'fresh', ageMs };
    if (ageMs <= SCREENSHOT_STALE_AFTER_MS) return { level: 'warn', ageMs };
    return { level: 'stale', ageMs };
}

// Чип давности в подвале плитки. Жёлтое и красное различаются не только цветом: иконка и
// жирность разные, у красного ещё и блёклый снимок (см. devices__screen_stale).
function ScreenAge({ screenshotAt, freshness, isNarrow }) {
    const { level, ageMs } = freshness;
    const Icon = { warn: ScheduleIcon, stale: WarningAmberIcon }[level] ?? null;
    const label = { warn: 'снимок устарел', stale: 'снимок давно не обновлялся' }[level];
    const text = ageMs === null ? 'нет снимка' : formatAge(ageMs, isNarrow);

    return <time
        className={`devices__screen-time devices__screen-time_${level}`}
        dateTime={screenshotAt ?? undefined}
        title={screenshotAt ? `Снимок: ${DateLib.formatterDateTime(screenshotAt)}` : 'Снимка нет'}
    >
        {Icon ? <Icon fontSize="inherit" titleAccess={label}/> : null}
        {text}
    </time>;
}

// Строка аккаунта в плитке: «● логин · статус ↗». Без перепривязки — за ней в таблицу. Текст —
// целиком, с переносом (devices__screen-line_wrap). На телефоне без ↗: зона нажатия слишком мелкая.
function ScreenAccountLine({ device, accounts, isNarrow }) {
    if (!device.external_id) {
        return <div className="devices__screen-line devices__screen-line_empty">не привязан</div>;
    }
    const account = accounts[String(device.external_id)] ?? null;
    const login = account?.label_show ?? account?.login ?? `#${device.external_id}`;
    const title = [login, account?.status].filter(Boolean).join(' · ');

    return <div className="devices__screen-line devices__screen-line_wrap" title={title}>
        {account ? <i
            className={`devices__account-dot devices__account-dot_${account.is_online ? 'online' : 'offline'}`}
            role="img"
            aria-label={account.is_online ? 'Аккаунт онлайн' : 'Аккаунт не в сети'}
        /> : null}
        <span className="devices__screen-line-main">{login}</span>
        {account?.status ? <span className="devices__screen-line-side">{account.status}</span> : null}
        {(!isNarrow && account?.url) ? <a
            className="devices__icon-action"
            href={account.url}
            target="_blank"
            rel="noopener noreferrer"
            title="Открыть карточку аккаунта в новой вкладке"
        >
            <OpenInNewIcon fontSize="inherit"/>
        </a> : null}
    </div>;
}

// Статус скрипта в плитке — иконкой и её цветом: пилюля-бейдж в 200px не влезает.
const SCRIPT_STATUS_ICONS = {
    'pending': ScheduleIcon,
    'running': PlayArrowIcon,
    'success': CheckIcon,
    'error': ErrorOutlineIcon,
    'cancelled': CloseIcon,
};

// Строка последнего скрипта в плитке: «[иконка] имя … 15 hours ago». created_ago — строка сервера,
// сама не тикает (обновится со списком). На телефоне — без неё.
function ScreenScriptLine({ lastScript, isNarrow }) {
    if (!lastScript) return <div className="devices__screen-line devices__screen-line_empty">—</div>;
    const Icon = SCRIPT_STATUS_ICONS[lastScript.status] ?? ScheduleIcon;
    const statusLabel = SCRIPT_STATUS_LABELS[lastScript.status] ?? lastScript.status;

    return <div
        className={`devices__screen-line devices__screen-line_status_${lastScript.status}`}
        title={`${lastScript.name} — ${statusLabel}${lastScript.created_ago ? `, ${lastScript.created_ago}` : ''}`}
    >
        <Icon className="devices__screen-line-icon" fontSize="inherit" titleAccess={statusLabel}/>
        <span className="devices__screen-line-main">{lastScript.name}</span>
        {(!isNarrow && lastScript.created_ago)
            ? <span className="devices__screen-line-side">{lastScript.created_ago}</span>
            : null}
    </div>;
}

// Ячейка «Замер»: последний бенчмарк устройства (last_benchmark — {type, value, created_ago} или
// null; приходит только в списке). Балл — ссылка на все замеры устройства в «Бенчмарках».
function LastBenchmarkCell({ device }) {
    const benchmark = device.last_benchmark;
    // null — устройство ещё не мерили: прогон PCMark идёт 12–20 минут и ставится вручную,
    // так что у большинства поле пустое — это норма, а не ошибка.
    if (!benchmark) return <span>—</span>;

    // value — итоговый балл (сумма подтестов). Разбивка — в карточке замера, история — в разделе.
    // Давность — в подсказке: колонка сжата до числа.
    const title = `Все замеры устройства${benchmark.created_ago ? ` · замер ${benchmark.created_ago}` : ''}`;
    return <Link to={benchmarksUrl(device.id)} title={title}>{benchmark.value}</Link>;
}

// Ячейка «Последний скрипт»: только чтение — имя (лейбл или, если не задан, техническое имя),
// статус запуска и сколько прошло с его постановки. lastScript — {name, status, created_ago}
// или null (см. DeviceResource::last_script); для Multi-скрипта учитывается ТОЛЬКО родитель
// (см. DevicesController::lastScriptSubquery — parent_execute_id IS NULL), дети сами по себе
// в колонку не попадают.
//
// created_ago приходит уже готовой строкой (Carbon::diffForHumans на локали приложения), а не
// датой — форматировать тут нечего, выводим как есть.
//
// Статус — точкой цвета статуса, а не пилюлей «имя (статус)»: пилюля была самой широкой вещью
// строки. «ошибка» — ещё и словом: её ищут глазами, до подсказки надо дотянуться мышью.
function LastScriptCell({ lastScript }) {
    if (!lastScript) return <span>—</span>;
    const statusLabel = SCRIPT_STATUS_LABELS[lastScript.status] ?? lastScript.status;

    return <span
        className="devices__last-script"
        title={`${lastScript.name} — ${statusLabel}${lastScript.created_ago ? `, ${lastScript.created_ago}` : ''}`}
    >
        <span className="devices__last-script-line">
            <i className={`devices__status-dot devices__status-dot_${lastScript.status}`}
                role="img" aria-label={statusLabel}/>
            <span className="devices__last-script-name">{lastScript.name}</span>
            {lastScript.status === 'error' ? <span className="devices__last-script-error">ошибка</span> : null}
        </span>
        {lastScript.created_ago
            ? <span className="devices__last-script-ago">{lastScript.created_ago}</span>
            : null}
    </span>;
}

// Провижн в «Состоянии»: ревизия android-farm, с которой развёрнуто устройство (DeviceResource::
// provision_version, пишется из ответа run.py на install/reinstall/update). В таблице — только
// проблема: null — устройство развёрнуто ДО появления версионности, то есть отстало. Саму ревизию
// показывает карточка устройства.
function ProvisionOutdatedBadge({ version }) {
    if (version) return null;
    return <span className="badge badge_warn" title="Развёрнуто до версионности провижна — устарело">
        устарело
    </span>;
}

// Ячейка «Аккаунт»: строка «● логин (статус) устройство ↗». Клик по логину → модалка со Select
// аккаунтов сервиса acc.rbx.effps.com, выбор сразу сохраняет devices.external_id (обычный PATCH,
// без фоновой задачи — быстро).
//
// accounts — {id: {login, label_show, label_select, status, is_online, url, bound_device}}.
// label_show / label_select собирает AccountsController; status, is_online, url — сервис аккаунтов.
// bound_device (id+name) добавляет наш бэкенд по devices.external_id.
function AccountCell({ device, canEdit, accounts, onRebind }) {
    const contextList = useContext(ContextList);
    const [_open, set_open] = useState(false);
    const account = device.external_id ? accounts[String(device.external_id)] : null;
    const label = device.external_id
        ? (account?.label_show ?? account?.login ?? `#${device.external_id}`)
        : 'не привязан';

    // Аккаунт числится за ДРУГИМ устройством. bound_device наш бэкенд строит по devices.external_id,
    // поэтому в ячейке того же устройства расхождение означает ровно одно: на аккаунт ссылаются два
    // устройства, и в справочник попало не это. Красный — сигнал разобрать задвоенную привязку.
    const boundDevice = account?.bound_device ?? null;
    const isBoundElsewhere = boundDevice !== null && String(boundDevice.id) !== String(device.id);

    return <>
        <span className="devices__account">
            {/* is_online берём из ответа как есть — двумя состояниями, без своих домыслов о «свежести». */}
            {account ? <i
                className={`devices__account-dot devices__account-dot_${account.is_online ? 'online' : 'offline'}`}
                title={account.is_online ? 'Аккаунт онлайн' : 'Аккаунт не в сети'}
            /> : null}

            {canEdit
                ? <span className="devices__editable devices__account-login" title={`${label} — клик, чтобы привязать другой`}
                    onClick={() => set_open(true)}>
                    {label}
                </span>
                : <span className="devices__account-login" title={label}>{label}</span>}

            {account?.status ? <span className="devices__account-status">({account.status})</span> : null}

            {boundDevice ? <span
                className={`devices__account-device${isBoundElsewhere ? ' devices__account-device_mismatch' : ''}`}
                title={isBoundElsewhere
                    ? 'Аккаунт числится за другим устройством — привязка задвоена'
                    : 'Устройство, за которым числится аккаунт'}
            >
                {boundDevice.name}
            </span> : null}

            {account?.url ? <a
                className="devices__icon-action"
                href={account.url}
                target="_blank"
                rel="noopener noreferrer"
                title="Открыть карточку аккаунта в новой вкладке"
            >
                <OpenInNewIcon fontSize="small"/>
            </a> : null}
        </span>

        {_open ? (
            <AccountModal
                device={device}
                accounts={accounts}
                onClose={() => set_open(false)}
                onDone={() => { onRebind(); contextList.reload(); }}
            />
        ) : null}
    </>;
}

function AccountModal({ device, accounts, onClose, onDone }) {
    const [_busy, set_busy] = useState(false);
    const [_error, set_error] = useState(null);

    // Аккаунт занят ДРУГИМ устройством — блокируем выбор (см. Select disabled/hint) и подсказываем
    // именем устройства, куда он привязан. СВОЙ текущий выбор (device.external_id) не блокируем —
    // иначе не открыть повторно уже привязанный на этом же устройстве аккаунт.
    const options = {};
    Object.entries(accounts).forEach(([id, account]) => {
        const boundElsewhere = account.bound_device && String(account.bound_device.id) !== String(device.id);
        // Из статусов показываем только «Заблокирован» — красной подсказкой (hint у Select красный):
        // выбирая аккаунт устройству, заблокированный надо видеть сразу, остальные статусы — шум.
        const isLocked = account.status === ACCOUNT_STATUS_FILTER_OPTIONS.locked;
        const hints = [
            ...(isLocked ? [`(${account.status})`] : []),
            ...(boundElsewhere ? [`(${account.bound_device.name})`] : []),
        ];
        const selectLabel = account.label_select ?? account.login;
        options[id] = (boundElsewhere || isLocked)
            ? { label: selectLabel, disabled: boundElsewhere, hint: hints.join(' ') }
            : selectLabel;
    });

    // Выбор аккаунта из селекта сразу сохраняет — без отдельной кнопки «Применить» (как у тарифа).
    const handlePick = async (accountId) => {
        if (_busy) return;
        set_busy(true);
        set_error(null);
        const data = await ApiDevice.Update(device.id, { external_id: accountId || null }).getResponse();
        set_busy(false);
        if (data) {
            onDone();
            onClose();
        } else {
            set_error('Не удалось сохранить привязку');
        }
    };

    return <Modal isOpenModal={true} closeModal={onClose} title={`Привязать аккаунт: ${device.name}`}>
        <div className="devices__change-type-hint">
            Выберите аккаунт из сервиса Roblox-аккаунтов — сохранится сразу. «Не выбрано» — отвязать.
            Занятые другим устройством аккаунты недоступны — сначала отвяжите их там.
        </div>
        <Select className="input_wf" options={options} value={device.external_id ?? ''} isNull={true} readOnly={_busy}
            onChange={(e) => handlePick(e.target.value)}>Аккаунт</Select>
        {_busy ? <div className="devices__change-type-hint">Сохранение…</div> : null}
        {_error ? <div className="devices__change-type-error">{_error}</div> : null}
    </Modal>;
}

// Подпись прокси из app-init — «EU\\EE 77.83.30.114 (2)», «— 1.2.3.4 (0)» (ещё не проверяли), выключенный —
// с « (выключен)». Разбираем на части для компактной ячейки; не разобралась — показываем как есть.
const PROXY_LABEL_PATTERN = /^(?:(\S+)\\(\S+)|—)\s+(\S+)\s+\((\d+)\)( \(выключен\))?$/;

function parseProxyLabel(label) {
    const match = PROXY_LABEL_PATTERN.exec(label ?? '');
    if (!match) return null;
    return {
        region: match[1] ?? null,
        countryCode: match[2] ?? null,
        host: match[3],
        devicesCount: Number(match[4]),
        isInactive: Boolean(match[5]),
    };
}

// Статус установки прокси на устройство → точка цвета статуса (как у «Последнего скрипта»).
// Идущие установка и снятие пульсируют: они «живые», ячейка сама обновится (ProxySetupPoller).
const PROXY_STATUS_DOTS = {
    'ok': 'success',
    'setup': 'pending',
    'removing': 'pending',
    'error': 'error',
};

// Режим прокси — коротким тегом у каждого устройства с прокси: «TCP+UDP» приглушённо (норма),
// «TCP» жёлтым (UDP — сама игра — идёт с адреса ноды, у устройства два выходных адреса).
const PROXY_MODE_TAGS = { 'tcp_udp': 'TCP+UDP', 'tcp': 'TCP' };

// Ячейка «Прокси» — в две строки: «● 77.83.30.114 ↻» и мелко «EU\\EE ×2 TCP+UDP». Точка — статус
// установки (подпись — в подсказке), адрес (клик — модалка выбора), ↻ — только при ошибке; ниже —
// регион\\страна выхода, сколько устройств на прокси и режим. Ошибка при назначенном прокси — не встал: ↻ ставит заново (PATCH
// с тем же proxy_id). Ошибка без прокси — туннель не снялся с ноды: ↻ снимает снова (proxy_id: null).
function ProxyCell({ device, canEdit, proxies, deviceProxyStatuses, deviceProxyModes }) {
    const contextList = useContext(ContextList);
    const [_open, set_open] = useState(false);
    const [_isRetrying, set_isRetrying] = useState(false);

    const fullLabel = device.proxy_id ? (proxies[String(device.proxy_id)] ?? `#${device.proxy_id}`) : null;
    const parsed = fullLabel ? parseProxyLabel(fullLabel) : null;
    const status = device.proxy_status;
    const statusLabel = status ? (deviceProxyStatuses[status] ?? status) : null;

    const handleRetry = async () => {
        if (_isRetrying) return;
        set_isRetrying(true);
        try {
            const data = await ApiDevice.Update(device.id, { proxy_id: device.proxy_id ?? null }).getResponse();
            if (data) contextList.reload();
        } finally {
            set_isRetrying(false);
        }
    };

    const statusTitle = {
        'ok': 'Установлен',
        'setup': 'Устанавливается (5–15 мин)',
        'removing': 'Снимается с ноды',
        'error': device.proxy_id
            ? 'Ошибка установки — причина в задаче «Установка прокси» на странице «Задачи»'
            : 'Не снялся с ноды — причина в задаче «Снятие прокси» на странице «Задачи»',
    }[status] ?? statusLabel;

    // Повтор установки — только если прокси ещё в справочнике (удалённый бэкенд отверг бы 422).
    const canRetry = canEdit && (status === 'error')
        && (!device.proxy_id || (String(device.proxy_id) in proxies));

    // Без прокси: «не назначен», а если снятие идёт или не удалось — словами.
    const emptyText = { 'removing': 'снимается', 'error': 'не снялся' }[status] ?? 'не назначен';
    const addressTitle = parsed
        ? `${fullLabel}${canEdit ? ' — клик, чтобы сменить прокси' : ''}`
        : (fullLabel ?? (canEdit ? 'Клик — назначить прокси' : ''));
    const address = fullLabel
        ? (parsed ? parsed.host : fullLabel)
        : emptyText;

    return <>
        <span className="devices__proxy">
            {/* Строка 1 — главное: статус, адрес (клик — выбор прокси), повтор при ошибке. */}
            <span className="devices__proxy-line">
                {status ? <i
                    className={`devices__status-dot devices__status-dot_${PROXY_STATUS_DOTS[status] ?? 'pending'}${(status === 'setup') || (status === 'removing') ? ' devices__status-dot_pulse' : ''}`}
                    role="img" aria-label={statusLabel} title={statusTitle}
                /> : null}

                <span
                    className={`devices__proxy-label${canEdit ? ' devices__editable' : ''}${fullLabel ? '' : ' devices__proxy-label_empty'}${parsed?.isInactive ? ' devices__proxy-label_inactive' : ''}${status === 'error' ? ' devices__proxy-label_error' : ''}`}
                    title={addressTitle}
                    onClick={canEdit ? () => set_open(true) : undefined}
                >
                    {address}
                </span>

                {canRetry ? <button
                    type="button"
                    className="devices__icon-action devices__proxy-retry"
                    disabled={_isRetrying}
                    onClick={handleRetry}
                    title={device.proxy_id ? 'Поставить прокси заново' : 'Снова снять прокси с ноды'}
                    aria-label={device.proxy_id ? 'Поставить прокси заново' : 'Снова снять прокси с ноды'}
                >
                    <ReplayIcon fontSize="inherit" className={_isRetrying ? 'devices__spin' : undefined}/>
                </button> : null}
            </span>

            {/* Строка 2 — мелко: регион\страна выхода, сколько устройств на прокси, режим. */}
            {device.proxy_id ? <span className="devices__proxy-line devices__proxy-line_details">
                {parsed ? <span className="devices__proxy-country"
                    title={parsed.countryCode ? 'Регион \\ страна выхода' : 'Страна выхода ещё не измерена'}>
                    {parsed.region || parsed.countryCode ? `${parsed.region ?? '—'}\\${parsed.countryCode ?? '—'}` : '—'}
                </span> : null}

                {parsed ? <span className="devices__proxy-count" title={`Устройств на этом прокси: ${parsed.devicesCount}`}>
                    ×{parsed.devicesCount}
                </span> : null}

                <span
                    className={`devices__proxy-mode${(device.proxy_mode ?? 'tcp_udp') === 'tcp' ? ' devices__proxy-mode_tcp' : ''}`}
                    title={(device.proxy_mode ?? 'tcp_udp') === 'tcp'
                        ? `${deviceProxyModes.tcp ?? 'Только TCP'}: UDP (игра) идёт с адреса ноды — у устройства два выходных адреса`
                        : `${deviceProxyModes.tcp_udp ?? 'TCP + UDP'}: через прокси идёт весь трафик`}
                >
                    {PROXY_MODE_TAGS[device.proxy_mode ?? 'tcp_udp'] ?? device.proxy_mode}
                </span>

                {parsed?.isInactive ? <span className="badge badge_warn" title="Прокси выключен в справочнике">выкл</span> : null}
            </span> : null}
        </span>

        {_open ? (
            <ProxyModal
                device={device}
                onClose={() => set_open(false)}
                onDone={() => contextList.reload()}
            />
        ) : null}
    </>;
}

// Пункты выбора прокси: заголовок группы (не выбирается), затем её прокси в порядке бэкенда
// (регион, адрес). Несколько групп — по названию, «Без группы» в конце. Одну группу без имени
// не подписываем: все массовые, заголовок ни о чём не сказал бы.
// deviceMode — выбранный режим устройства: в «TCP + UDP» прокси «только TCP» (proxies.mode = tcp)
// недоступны — бэкенд ответил бы 422 на proxy_mode; показываем их, но выбрать нельзя.
function proxyOptionsByGroup(data, proxyGroups, deviceMode) {
    const buckets = new Map();
    data.forEach((option) => {
        const key = option.proxy_group_id == null ? 'none' : String(option.proxy_group_id);
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(option);
    });
    const keys = [...buckets.keys()].sort((a, b) => {
        if (a === 'none') return 1;
        if (b === 'none') return -1;
        const left = proxyGroups[a] ?? `#${a}`;
        const right = proxyGroups[b] ?? `#${b}`;
        return String(left).localeCompare(String(right), 'ru');
    });
    const showHeaders = keys.length > 1 || (keys.length === 1 && keys[0] !== 'none');
    const options = [];
    keys.forEach((key) => {
        if (showHeaders) {
            const label = key === 'none' ? 'Без группы' : (proxyGroups[key] ?? `#${key}`);
            options.push([`__group:${key}`, { label, header: true }]);
        }
        buckets.get(key).forEach((option) => {
            const isTcpOnlyBlocked = (option.mode === 'tcp') && (deviceMode === 'tcp_udp');
            let value = option.label;
            if (isTcpOnlyBlocked) {
                value = { label: option.label, disabled: true, hint: '(только TCP)' };
            } else if (option.is_current && !option.available) {
                value = { label: option.label, hint: '(текущий; новым назначением недоступен)' };
            }
            options.push([String(option.id), value]);
        });
    });
    return options;
}

// Выбор прокси устройству. Варианты — не весь справочник app-init, а GET devices/{id}/proxy-options:
// в списке — только включённые прокси, доступные ноде устройства (без группы или из групп,
// привязанных к ноде); недоступный ноде бэкенд отклонит 422. Порядок осмысленный (регион, адрес) —
// поэтому варианты уходят в Select массивом пар, а не объектом (тот переставил бы их по id).
// Режим прокси (devices.proxy_mode): «TCP + UDP» — весь трафик через прокси (нужен играм: Roblox
// играет по UDP), «Только TCP» — UDP идёт напрямую с адреса ноды. У устройства режима ещё нет — в
// выборе по умолчанию «Только TCP» (решение оператора); с ним доступны и прокси «только TCP».
const PROXY_MODE_DEFAULT = 'tcp';

function ProxyModal({ device, onClose, onDone }) {
    const [_busy, set_busy] = useState(false);
    const [_error, set_error] = useState(null);
    const [_proxyOptions, set_proxyOptions] = useState(null); // ответ proxy-options; null — грузятся
    const proxyGroups = useSelector((state) => state.option.proxyGroups);
    const deviceProxyModes = useSelector((state) => state.option.deviceProxyModes);
    // Режим выбирают ДО прокси: выбор прокси сохраняется сразу и уносит с собой выбранный режим.
    const currentMode = device.proxy_mode ?? PROXY_MODE_DEFAULT;
    const [_mode, set_mode] = useState(currentMode);
    const isModeChanged = _mode !== currentMode;

    useEffect(() => {
        let isActual = true;
        (async () => {
            const api = ApiDevice.ProxyOptions(device.id);
            api.__showErrors = false; // ошибка — текстом в модалке, без дубля тостом
            const data = await api.getResponse();
            if (!isActual) return;
            if (data === null) {
                set_error('Не удалось загрузить список прокси');
                set_proxyOptions([]);
                return;
            }
            set_proxyOptions(data);
        })();
        return () => { isActual = false; };
    }, [device.id]);

    // Варианты пересчитываются при смене режима: прокси «только TCP» доступны лишь в «Только TCP».
    // Текущий есть в списке всегда, даже если уже недоступен ноде или выключен: выбор показывает,
    // что стоит сейчас.
    const _options = useMemo(
        () => (_proxyOptions === null ? null : proxyOptionsByGroup(_proxyOptions, proxyGroups, _mode)),
        [_proxyOptions, proxyGroups, _mode],
    );
    // Текущий прокси умеет только TCP — «TCP + UDP» для него бэкенд не примет.
    const isCurrentProxyTcpOnly = (_proxyOptions ?? []).some((option) => option.is_current && (option.mode === 'tcp'));
    const hasTcpOnlyProxies = (_proxyOptions ?? []).some((option) => option.mode === 'tcp');

    // Назначение — вместе с режимом; снятие (proxy_id: null) режим бэкенд сбрасывает сам, его не шлём.
    // Только режим (прокси тот же) — PATCH {proxy_mode}: назначенный прокси переставится в новом режиме.
    const send = async (params, fallbackError) => {
        set_busy(true);
        set_error(null);
        const api = ApiDevice.Update(device.id, params);
        // Ошибку показываем в самой модалке — тост с тем же текстом был бы лишним.
        api.__showErrors = false;
        const data = await api.getResponse();
        set_busy(false);
        if (data) {
            onDone();
            onClose();
            return;
        }
        set_error(api.errors?.proxy_id?.[0] ?? api.errors?.proxy_mode?.[0] ?? fallbackError);
    };

    const save = (proxyId) => send(
        proxyId ? { proxy_id: proxyId, proxy_mode: _mode } : { proxy_id: null },
        proxyId ? 'Не удалось назначить прокси' : 'Не удалось снять прокси',
    );

    const saveMode = () => send({ proxy_mode: _mode }, 'Не удалось сменить режим');

    // Выбор сразу сохраняет — без отдельной кнопки «Применить» (как у аккаунта и тарифа).
    const handlePick = (proxyId) => {
        if (_busy) return;
        // Тот же прокси в том же режиме бэкенд проигнорирует — кроме ошибки (повтор установки). Не
        // шлём запрос впустую. Тот же прокси в другом режиме — переустановка в новом режиме.
        const isSameProxy = proxyId === String(device.proxy_id ?? '');
        // «Не выбрано» без прокси — режим тут ни при чём: снимать нечего.
        if (isSameProxy && ((proxyId === '') || !isModeChanged) && (device.proxy_status !== 'error')) {
            onClose();
            return;
        }
        // «Не выбрано» на уже снимающемся — снятие и так идёт.
        if ((proxyId === '') && (device.proxy_status === 'removing')) {
            onClose();
            return;
        }
        save(proxyId);
    };

    return <Modal isOpenModal={true} closeModal={onClose} title={`Прокси: ${device.name}`}>
        <div className="devices__change-type-hint">
            Выбор прокси сохраняется сразу (с выбранным режимом), установка идёт в фоне 5–15 минут
            (устройство перезагрузится). Перед назначением стоит проверить прокси в разделе «Прокси».
            В списке — только прокси, доступные ноде устройства: без группы или из групп, привязанных к
            ноде. «Не выбрано» — снять прокси: назначение снимается сразу, туннель с ноды убирает
            фоновая задача (пока она идёт — статус «Снимается»).
        </div>
        <Select className="input_wf" options={deviceProxyModes} value={_mode} readOnly={_busy}
            onChange={(e) => set_mode(e.target.value)}>Режим</Select>
        {_mode === 'tcp' ? <div className="devices__change-type-error">
            У устройства будет два выходных адреса: прокси — для TCP, нода — для UDP. Вход и загрузка
            пойдут с адреса прокси, а сама игра — с адреса ноды, и связать их между собой тривиально.
            Режим нужен, только если стабильность важнее единого адреса.
        </div> : null}
        {((_mode === 'tcp_udp') && hasTcpOnlyProxies) ? <div className="devices__change-type-hint">
            Прокси «только TCP» в этом режиме выбрать нельзя — для них нужен режим «Только TCP».
        </div> : null}
        {(device.proxy_id && isModeChanged) ? <div className="devices__proxy-mode-actions">
            <Button className="btn-warn" disabled={_busy || ((_mode === 'tcp_udp') && isCurrentProxyTcpOnly)}
                onClick={saveMode}>Сменить режим</Button>
            <span className="devices__change-type-hint">
                {((_mode === 'tcp_udp') && isCurrentProxyTcpOnly)
                    ? 'текущий прокси умеет только TCP — для «TCP + UDP» выберите другой прокси'
                    : 'прокси останется тем же и переустановится (5–15 мин)'}
            </span>
        </div> : null}
        {_options === null
            ? <Loader text="Прокси…"/>
            : <Select className="input_wf" options={_options} value={device.proxy_id ?? ''} isNull={true}
                readOnly={_busy} onChange={(e) => handlePick(e.target.value)}>Прокси</Select>}
        {_busy ? <div className="devices__change-type-hint">Сохранение…</div> : null}
        {_error ? <div className="devices__change-type-error">{_error}</div> : null}
    </Modal>;
}

// Перечитывает список, пока на странице есть устройство, где прокси ставится или снимается.
function ProxySetupPoller() {
    const contextList = useContext(ContextList);
    const hasSetup = (contextList.list ?? []).some((row) => PROXY_PENDING_STATUSES.includes(row.proxy_status));
    const reload = contextList.reload;

    useEffect(() => {
        if (!hasSetup) return undefined;
        const timer = setInterval(reload, PROXY_SETUP_POLL_MS);
        return () => clearInterval(timer);
    }, [hasSetup, reload]);

    return null;
}

// Чекбокс "выбрать все" — только строки ТЕКУЩЕЙ страницы списка (contextList.list).
function SelectAllCheckbox({ selectedIds, setSelectedIds, children }) {
    const contextList = useContext(ContextList);
    const pageIds = (contextList?.list ?? []).map((row) => row.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

    const handleChange = (e) => {
        if (e.target.checked) {
            setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
        } else {
            setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
        }
    };

    return <InputCheckbox value={allSelected} onChange={handleChange}>{children}</InputCheckbox>;
}

// Действия над выбранными устройствами — смысловые колонки (MM2, Аккаунт, Контейнер, Обслуживание,
// раскладка — BATCH_SECTIONS): частое — кнопками на виду, редкое — в «Ещё ▾», опасное — под красным
// раскрытием. Действия и их правила — из каталога бэкенда (GET devices/batch-actions). Подтверждение —
// только у опасных (danger ≥ 2) и при «Сразу».
// Под строкой — ход по номерам запусков (tasks/statuses, обе семьи), отмена пачкой, пропущенные и
// упавшие с причинами.
// onAccountsChanged — перечитать карту аккаунтов страницы: перенос перепривязывает аккаунт в сервисе
// аккаунтов, и без этого у приёмника аккаунт краснел бы как «задвоенный» до перезагрузки.
function DevicesBatchBar({ selectedIds, onClear, onDeselect, onAccountsChanged }) {
    const contextList = useContext(ContextList);
    const [_catalog, set_catalog] = useState(null); // {byName, sections}; null — грузится
    const [_link, set_link] = useState(''); // ссылка на скрипт — общее поле колонки MM2
    // «Сразу» (только колонка MM2) — без ожидания тика очереди; ничего не отменяет. После запуска сбрасывается.
    const [_immediate, set_immediate] = useState(false);
    const [_dispatching, set_dispatching] = useState(null); // имя действия, пока идёт запуск
    const [_confirm, set_confirm] = useState(null); // {action, isImmediate} — ждёт подтверждения
    const [_dangerOpen, set_dangerOpen] = useState({}); // {ключ колонки: раскрыты ли «Опасные»}
    const [_mobileTab, set_mobileTab] = useState(null); // телефон: открытая вкладка-колонка
    const isNarrow = useIsNarrow();
    const [_modal, set_modal] = useState(null); // 'transfer' | 'install-apk' | null
    // Итог последнего запуска: {label, error?, text?} — error: отказ целиком, text: итог без хода.
    const [_run, set_run] = useState(null);
    const [_executes, set_executes] = useState(null); // {family, ids} — номера запусков для хода
    const [_statuses, set_statuses] = useState([]);   // ход по номерам: [{id, device_id, status, finished, error}]
    // Устройства, на которые действие НЕ встало: [[deviceId, причина]].
    const [_skippedReasons, set_skippedReasons] = useState([]);
    const [_isOpenDetails, set_isOpenDetails] = useState(false);
    const [_isCancelling, set_isCancelling] = useState(false);
    const [_cancelError, set_cancelError] = useState(null);
    const [_isCatalogFailed, set_isCatalogFailed] = useState(false);
    // Свёрнута ли панель. На телефоне стартуем свёрнутыми — сначала нужен список.
    const [_isOpen, set_isOpen] = useState(() => !isNarrowScreen());
    const pollTimer = useRef(null);
    // Номер текущего запуска: ответ хода от прежнего (закрыт, сменён, панель ушла) отбрасывается —
    // иначе поздний ответ перезаписал бы ход нового и завёл бы второй таймер.
    const runSeq = useRef(0);

    useEffect(() => () => {
        runSeq.current += 1;
        clearTimeout(pollTimer.current);
    }, []);

    // Каталог — при появлении панели (бар монтируется на новое выделение).
    const loadCatalog = useCallback(async () => {
        set_isCatalogFailed(false);
        const groups = await ApiDevice.BatchActions().getResponse();
        if (groups === null) {
            set_isCatalogFailed(true);
            return;
        }
        set_catalog(buildCatalog(groups));
    }, []);

    useEffect(() => { loadCatalog(); }, [loadCatalog]);

    // Имя устройства по id. Ключи в ответах — строки, поэтому сравнение через String().
    // Список — текущая страница (выделение может быть шире), поэтому запасной вариант «#id».
    const getDeviceName = (deviceId) => (contextList?.list ?? [])
        .find((item) => String(item.id) === String(deviceId))?.name ?? `#${deviceId}`;

    // Ход по номерам — пока есть незавершённые. seq — номер запуска, к которому относится опрос.
    const pollStatuses = async (family, ids, seq) => {
        clearTimeout(pollTimer.current);
        const response = await ApiTask.Statuses({ [family]: ids });
        if (seq !== runSeq.current) return;
        if (response === null) {
            set_run((previous) => (previous ? { ...previous, error: 'не удалось получить ход — смотрите «Задачи»' } : previous));
            return;
        }
        const data = response.data ?? [];
        set_statuses(data);
        if (data.some((item) => !item.finished)) {
            pollTimer.current = setTimeout(() => pollStatuses(family, ids, seq), SCRIPT_POLL_MS);
        }
    };

    const resetRun = () => {
        runSeq.current += 1;
        clearTimeout(pollTimer.current);
        set_cancelError(null);
        set_run(null);
        set_executes(null);
        set_statuses([]);
        set_skippedReasons([]);
        set_isOpenDetails(false);
    };

    // Выполнить действие — уже после подтверждения.
    const execute = async (target, isImmediate) => {
        resetRun();
        set_dispatching(target.name);
        try {
            if (target.name === 'manual-on' || target.name === 'manual-off') {
                // Не скрипт: флаг is_manual в строках, без хода.
                const data = await ApiDevice.BatchManual(selectedIds, target.name === 'manual-on').getResponse();
                if (data) contextList.reload();
                return;
            }

            const body = { device_ids: selectedIds };
            if (target.family !== 'panel') {
                body.immediate = isImmediate;
                if (isLinkAllowed(target) && _link.trim()) body.link = _link.trim();
                if (target.confirm_count) body.confirm_count = selectedIds.length;
            }
            const { ok, status, data } = await ApiDevice.BatchRun(target.endpoint, body);
            if (!ok) {
                // Отказ каталога — {error}; валидация Laravel — {message, errors}.
                set_run({ label: target.label, error: data?.error ?? data?.message ?? `не удалось запустить (код ${status})` });
                return;
            }

            if (target.name === 'proxy-clear') {
                // Назначение снято сразу (туннель снимет крон) — перечитать список, чтобы было видно.
                const cleared = (data?.cleared_device_ids ?? []).length;
                const skipped = (data?.skipped_device_ids ?? []).length;
                set_run({ label: target.label, text: `снято ${cleared}${skipped > 0 ? `, снимать нечего ${skipped}` : ''}` });
                contextList.reload();
                return;
            }

            set_skippedReasons(Object.entries(data?.skipped_reasons ?? {}));
            // Номера есть и у пропущенных с not_started — их исход тоже по номеру.
            const ids = Object.values(data?.execute_ids ?? {});
            set_run({ label: target.label, text: ids.length === 0 ? 'ничего не запущено' : null });
            if (isImmediate) set_immediate(false);
            if (ids.length > 0) {
                const family = data.family ?? target.family;
                set_executes({ family, ids });
                pollStatuses(family, ids, runSeq.current);
            }
        } finally {
            set_dispatching(null);
        }
    };

    // Точка входа любого действия: модалки — сразу; опасные и «Сразу» — через подтверждение.
    const startAction = (target, section) => {
        if (!target || _dispatching) return;
        if (PANEL_MODAL_ACTIONS.includes(target.name)) {
            set_modal(target.name);
            return;
        }
        if (isLinkRequired(target) && !_link.trim()) return;
        const isImmediate = Boolean(section.hasImmediate && _immediate && target.immediate_allowed);
        if (needsConfirm(target, isImmediate)) {
            set_confirm({ action: target, isImmediate });
            return;
        }
        execute(target, isImmediate);
    };

    // Отмена незавершённых запусков пачкой.
    const handleCancel = async () => {
        if (!_executes || _isCancelling) return;
        const unfinishedIds = _statuses.filter((item) => !item.finished).map((item) => item.id);
        if (unfinishedIds.length === 0) return;
        set_isCancelling(true);
        set_cancelError(null);
        try {
            // Нужно право admin.tasks.edit — без него (и при сбое) говорим, а не молчим.
            const response = await ApiTask.CancelBatch({ [_executes.family]: unfinishedIds });
            if (response === null) set_cancelError('не удалось отменить (нужно право на задачи)');
            // Опрос уже идёт — он и покажет отменённые; новую цепочку не заводим.
        } finally {
            set_isCancelling(false);
        }
    };

    const deviceNames = selectedIds.map(getDeviceName);

    const isLinkMissing = (action) => isLinkRequired(action) && !_link.trim();

    // Инфографика — по строкам выбранных на текущей странице (выделение может быть шире).
    const selectedRows = (contextList?.list ?? []).filter((row) => selectedIds.includes(row.id));
    const summary = selectedRows.length > 0 ? summarizeDevices(selectedRows) : null;
    // Сводка полная — все выбранные на текущей странице; только тогда «не к чему применить» гасит кнопку.
    const isSummaryFull = selectedRows.length === selectedIds.length;

    // Предупреждение в подтверждении: на каких из выбранных действие, скорее всего, не сработает.
    const confirmWarning = (action) => {
        if (!summary) return null;
        const parts = [];
        if (summary.bad.length > 0 && action.family !== 'redroid') parts.push(`${summary.bad.length} остановлены / с ошибкой`);
        if (summary.noAccount.length > 0 && ['mm2', 'activity', 'auth'].includes(action.groupKey) && action.name !== 'register-account') {
            parts.push(`${summary.noAccount.length} без аккаунта`);
        }
        return parts.length > 0 ? `Из выбранных: ${parts.join(', ')} — на них действие, скорее всего, упадёт.` : null;
    };

    const renderActionButton = (action, section, { isDanger = false } = {}) => {
        const hint = isDanger ? null : actionHint(action, summary);
        return <BatchActionButton key={action.name} action={action}
            label={section.labels?.[action.name] ?? action.label}
            className={actionButtonClass(action, { isAccent: section.accent === action.name, isDanger })}
            hint={hint}
            isModal={PANEL_MODAL_ACTIONS.includes(action.name)}
            isBusy={_dispatching === action.name}
            disabled={!!_dispatching || isLinkMissing(action) || (Boolean(hint?.isEmpty) && isSummaryFull)}
            title={(hint?.isEmpty && isSummaryFull)
                ? 'Нет подходящих среди выбранных'
                : [action.label, action.note].filter(Boolean).join(' — ')}
            onClick={() => startAction(action, section)}/>;
    };

    // Колонка — одна анатомия: заголовок + «Ещё» → кнопки на виду → (вход колонки) → «Опасные».
    const renderSection = (section) => {
        const SectionIcon = section.icon;
        return <div className="devices__batch-section" key={section.key}>
            <div className="devices__batch-section-head">
                <div className="devices__batch-section-title">
                    {SectionIcon ? <SectionIcon className="devices__batch-btn-icon"/> : null}
                    {section.title}
                </div>
                <BatchMoreMenu actions={section.more} disabled={!!_dispatching} isItemDisabled={isLinkMissing}
                    onPick={(action) => startAction(action, section)}/>
            </div>
            <div className="devices__batch-section-body">
                {section.primary.map((action) => renderActionButton(action, section))}
                {section.toggle.length > 0 ? <>
                    <span className="devices__batch-field-hint">Кто управляет</span>
                    <div className="devices__batch-segment">
                        {section.toggle.map((action) => renderActionButton(action, section))}
                    </div>
                </> : null}
                {section.linkAction ? <>
                    <InputText value={_link} hasLabel={false} placeholder="Ссылка на скрипт"
                        className="devices__batch-link-input" onChange={(e) => set_link(e.target.value)}/>
                    {renderActionButton(section.linkAction, section)}
                    {isLinkMissing(section.linkAction) ? <span className="devices__batch-field-hint">
                        Вставьте ссылку, чтобы запустить
                    </span> : null}
                </> : null}
                {section.hasImmediate ? <InputCheckbox value={_immediate} title={IMMEDIATE_NOTE}
                    className={`devices__batch-immediate${_immediate ? ' devices__batch-immediate_on' : ''}`}
                    onChange={(e) => set_immediate(e.target.checked)}>
                    <BoltIcon className="devices__batch-btn-icon"/>
                    {_immediate ? 'Следующий запуск — без очереди' : 'Сразу, без очереди'}
                </InputCheckbox> : null}
            </div>
            <BatchDangerZone count={section.danger.length} isOpen={Boolean(_dangerOpen[section.key])}
                onToggle={() => set_dangerOpen((previous) => ({ ...previous, [section.key]: !previous[section.key] }))}>
                {section.danger.map((action) => renderActionButton(action, section, { isDanger: true }))}
            </BatchDangerZone>
        </div>;
    };

    // На телефоне колонки — вкладками: четыре колонки подряд закрывали весь список.
    const sections = _catalog?.sections ?? [];
    const visibleSections = isNarrow ? sections.filter((section) => section.key === (_mobileTab ?? sections[0]?.key)) : sections;

    const countStatus = (...statuses) => _statuses.filter((item) => statuses.includes(item.status)).length;
    const unfinishedCount = _statuses.filter((item) => !item.finished).length;
    // Подробности: пропущенные (причина бэкенда) и упавшие (ошибка запуска).
    const details = [
        ..._skippedReasons.map(([deviceId, reason]) => [`skip-${deviceId}`, deviceId, reason]),
        ..._statuses.filter((item) => item.status === 'error')
            .map((item) => [`error-${item.id}`, item.device_id, item.error ?? 'ошибка']),
    ];

    return <div className={`devices__batch-bar${isNarrow && _isOpen ? ' devices__batch-bar_sheet' : ''}`}>
        <div className="devices__batch-toolbar">
            <button type="button" className="devices__batch-toggle" onClick={() => set_isOpen((open) => !open)}
                title={_isOpen ? 'Свернуть' : 'Развернуть'}>
                {_isOpen ? <ExpandMoreIcon className="devices__batch-btn-icon"/> : <ExpandLessIcon className="devices__batch-btn-icon"/>}
                Действия
            </button>
            <span className="devices__batch-count">Выбрано: {selectedIds.length}</span>
            {summary ? <BatchSummary summary={summary} known={selectedRows.length} total={selectedIds.length}
                onDeselect={onDeselect}/> : null}
            <button type="button" className="devices__batch-clear" onClick={onClear}>
                <CloseIcon className="devices__batch-btn-icon"/>
                <span className="devices__batch-clear-text">Снять выбор</span>
            </button>
        </div>

        {!_isOpen ? null : _isCatalogFailed ? <span className="devices__batch-result">
            Не удалось загрузить действия.{' '}
            <button type="button" className="devices__batch-link-button" onClick={loadCatalog}>Повторить</button>
        </span> : _catalog === null ? <Loader className="loader_inline" text="Действия…"/> : <>
            {isNarrow ? <div className="devices__batch-tabs">
                {sections.map((section) => {
                    const SectionIcon = section.icon;
                    return <button type="button" key={section.key}
                        className={`devices__batch-tab${(visibleSections[0]?.key === section.key) ? ' devices__batch-tab_active' : ''}`}
                        onClick={() => set_mobileTab(section.key)}>
                        {SectionIcon ? <SectionIcon className="devices__batch-btn-icon"/> : null}
                        {section.shortTitle ?? section.title}
                    </button>;
                })}
            </div> : null}
            <div className="devices__batch-sections">{visibleSections.map(renderSection)}</div>
        </>}

        {/* Итог запуска: ход по номерам, отмена, пропущенные и упавшие — раскрываются. */}
        {_run ? <div className="devices__batch-feedback">
            {!_run.error ? <BatchProgress statuses={_statuses}/> : null}
            <span className="devices__batch-result">
                «{_run.label}»: {_run.error
                    ? <span className="devices__batch-skipped-item">{_run.error}</span>
                    : (_run.text ?? (_statuses.length === 0
                        ? 'запуск…'
                        : `ждёт / идёт ${countStatus('pending', 'running')} · успешно ${countStatus('success')} · ошибка ${countStatus('error')}`
                            + (countStatus('cancelled') > 0 ? ` · отменено ${countStatus('cancelled')}` : '')))}
                {details.length > 0 ? <>
                    {' · '}
                    <button type="button" className="devices__batch-link-button" onClick={() => set_isOpenDetails((open) => !open)}>
                        {_skippedReasons.length > 0 ? `пропущено ${_skippedReasons.length}` : 'причины'} {_isOpenDetails ? '▾' : '▸'}
                    </button>
                </> : null}
                {unfinishedCount > 0 ? <>
                    {' · '}
                    <button type="button" className="devices__batch-link-button" disabled={_isCancelling} onClick={handleCancel}>
                        {_isCancelling ? 'отмена…' : `отменить ${unfinishedCount}`}
                    </button>
                </> : null}
                {_cancelError ? <span className="devices__batch-skipped-item"> · {_cancelError}</span> : null}
                {' '}
                <button type="button" className="devices__batch-link-button" title="Скрыть" onClick={resetRun}>✕</button>
            </span>
            {_isOpenDetails && details.length > 0 ? <div className="devices__batch-skipped">
                {details.map(([key, deviceId, reason]) => (
                    <div className="devices__batch-skipped-item" key={key}>
                        {getDeviceName(deviceId)}: {reason}
                    </div>
                ))}
            </div> : null}
        </div> : null}

        {_confirm ? <BatchConfirmModal
            action={_confirm.action}
            deviceNames={deviceNames}
            isImmediate={_confirm.isImmediate}
            warning={confirmWarning(_confirm.action)}
            onClose={() => set_confirm(null)}
            onConfirm={() => { const { action, isImmediate } = _confirm; set_confirm(null); execute(action, isImmediate); }}
        /> : null}
        {_modal === 'transfer' ? <DeviceTransferModal
            sourceIds={selectedIds}
            onClose={() => set_modal(null)}
            onDone={() => { contextList.reload(); onAccountsChanged(); }}
        /> : null}
        {_modal === 'install-apk' ? <BatchApkModal
            deviceIds={selectedIds}
            onClose={() => set_modal(null)}
            onSkipped={(reasons, isQueued) => {
                resetRun();
                if (reasons.length > 0) {
                    set_run({ label: 'Установка APK', text: isQueued ? 'поставлено в очередь' : 'не поставлено' });
                    set_skippedReasons(reasons);
                }
            }}
        /> : null}
    </div>;
}
